/**
 * regex: `[а-яА-Я]+`
 *
 * Match a single character present in the list below `[а-яА-Я\-_]`
 * * `а-я` matches a single character in the range between `а` (index 1072) and `я` (index 1103) (case sensitive)
 * * `А-Я` matches a single character in the range between `А` (index 1040) and `Я` (index 1071) (case sensitive)
 *
 * `+` matches the previous token between `one` and `unlimited` times, as many times as possible, giving back as needed (greedy)
 */
export const hasCyrillicCharacters = /[а-яА-Я]+/gm;

/**
 * regex: `(?:<h[1-6]>)+(.*?)<\/h[1-6]>`
 *
 * String contains text found inside header tags
 *
 * Match any amount of `h` tags before capturing group
 * * `+` matches the previous token between one and unlimited times, as many times as possible, giving back as needed (greedy)
 * * `<h` matches the characters `<h` literally (case insensitive)
 * * `<` matches the character `<` with index 6010 (3C16 or 748) literally (case insensitive)
 * * `h` matches the character h with index 10410 (6816 or 1508) literally (case insensitive)
 * * `1-6` matches a single character in the range between `1` (index 49) and `6` (index 54) (case insensitive)
 * * `>` matches the character `>` with index 6210 (3E16 or 768) literally (case insensitive)
 *
 * Match any amount of any characters
 * * `.` matches any character (except for line terminators)
 * * `*?` matches the previous token between zero and unlimited times, as few times as possible, expanding as needed (lazy)
 *
 * Match a closing `h` tag(?:('|")(\w*\2)('|")(?=:))
 * * `<` matches the character `<` with index 6010 (3C16 or 748) literally (case insensitive)
 * * `\/` matches the character / with index 4710 (2F16 or 578) literally (case insensitive)
 * * `h` matches the character `h` with index 10410 (6816 or 1508) literally (case insensitive)
 * * `1-6` matches a single character in the range between `1` (index 49) and `6` (index 54) (case insensitive)
 * * `>` matches the character `>` with index 6210 (3E16 or 768) literally (case insensitive)
 */
export const hasHeadingTag = /(?:<h[1-6]>)+(.*?)<\/h[1-6]>/im;

/**
 * regex: `<\/?[^>]+(>|$)`
 *
 * `<` matches the character `<` with index 6010 (3C16 or 748) literally (case sensitive)
 * `\/` matches the character `/` with index 4710 (2F16 or 578) literally (case sensitive)
 * `?` matches the previous token between zero and one times, as many times as possible, giving back as needed (greedy)
 * Match a single character not present in the list below `[^>]`
 * `+` matches the previous token between one and unlimited times, as many times as possible, giving back as needed (greedy)
 * `>` matches the character `>` with index 6210 (3E16 or 768) literally (case sensitive)
 * 1st Capturing Group `(>|$)`
 * * 1st Alternative `>`
 * * `>` matches the character `>` with index 6210 (3E16 or 768) literally (case sensitive)
 * * 2nd Alternative `$`
 * * `$` asserts position at the end of the string, or before the line terminator right at the end of the string (if any)
 */
export const hasHTMLTag = /<\/?[^>]+(>|$)/g;

/** `\s` matches any whitespace character (equivalent to `[\r\n\t\f\v \u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000\ufeff]`) */
export const hasWhiteSpace = /\s/g;

/**
 * regex: `^[\w!#$%&'*+\-\/=?^`{|}~.]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$`
 *
 * /
 * `^` asserts position at start of the string
 *
 * Match a single character present in the list below [\w!#$%&'*+\-\/=?^`{|}~.]
 * `+` matches the previous token between one and unlimited times, as many times as possible, giving back as needed (greedy)
 * `\w` matches any word character (equivalent to [a-zA-Z0-9_])
 * `!#$%&'*+` matches a single character in the list !#$%&'*+ (case sensitive)
 * `\-` matches the character - with index 4510 (2D16 or 558) literally (case sensitive)
 * `\/` matches the character / with index 4710 (2F16 or 578) literally (case sensitive)
 * `=?^`{|}~.` matches a single character in the list =?^`{|}~. (case sensitive)
 * `@` matches the character @ with index 6410 (4016 or 1008) literally (case sensitive)
 *
 * Match a single character present in the list below [a-zA-Z0-9-]
 * `+` matches the previous token between one and unlimited times, as many times as possible, giving back as needed (greedy)
 * a-z matches a single character in the range between a (index 97) and z (index 122) (case sensitive)
 * A-Z matches a single character in the range between A (index 65) and Z (index 90) (case sensitive)
 * 0-9 matches a single character in the range between 0 (index 48) and 9 (index 57) (case sensitive)
 * `-` matches the character - with index 4510 (2D16 or 558) literally (case sensitive)
 * `\.` matches the character . with index 4610 (2E16 or 568) literally (case sensitive)
 *
 * Match a single character present in the list below [a-zA-Z]
 * `{2,}` matches the previous token between 2 and unlimited times, as many times as possible, giving back as needed (greedy)
 * a-z matches a single character in the range between a (index 97) and z (index 122) (case sensitive)
 * A-Z matches a single character in the range between A (index 65) and Z (index 90) (case sensitive)
 *
 * `$` asserts position at the end of the string
 */
export const isEmail = /^[\w!#$%&'*+\-/=?^`{|}~.]+@[a-zA-Z0-9-.]+\.[a-zA-Z]{2,}$/;

/**
 * regex: `^[0-9+\-\s()]+$`
 *
 * `^` asserts position at start of the string
 *
 * Match a single character present in the list below [0-9+\-\s()]
 * * `+` matches the previous token between one and unlimited times, as many times as possible, giving back as needed (greedy)
 * * `0-9` matches a single character in the range between 0 (index 48) and 9 (index 57) (case sensitive)
 * * `+` matches the character + with index 4310 (2B16 or 538) literally (case sensitive)
 * * `\-` matches the character - with index 4510 (2D16 or 558) literally (case sensitive)
 * * `\s` matches any whitespace character (equivalent to [\r\n\t\f\v ])
 * * `()` matches a single character in the list () (case sensitive)
 *
 * `$` asserts position at the end of the string, or before the line terminator right at the end of the string (if any)
 */
export const isPhoneNumber = /^[0-9+\-\s()]+$/;

/**
 * regex: `(.*)(?:<span\sclass=(?:'|"|\\")mktoErrorDetail(?:'|"|\\")>)(.*)(?:<\/span>)`
 *
 * 1st Capturing Group `(.*)`
 * * `.` matches any character (except for line terminators)
 * * `*` matches the previous token between zero and unlimited times, as many times as possible, giving back as needed (greedy)
 *
 * Non-capturing group `(?:<span\sclass=(?:'|"|\\")mktoErrorDetail(?:'|"|\\")>)`
 * `<span` matches the characters `<span` literally (case insensitive)
 * `\s` matches any whitespace character (equivalent to [\r\n\t\f\v \u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000\ufeff])
 * `class=` matches the characters `class=` literally (case insensitive)
 *
 * Non-capturing group `(?:'|"|\\")`
 * * 1st Alternative `'`
 * * *  `'` matches the character `'` with index 3910 (2716 or 478) literally (case insensitive)
 * * 2nd Alternative `"`
 * * * `"` matches the character `"` with index 3410 (2216 or 428) literally (case insensitive)
 * * 3rd Alternative `\\"`
 * * `\\` matches the character `\` with index 9210 (5C16 or 1348) literally (case insensitive)
 * `"` matches the character `"` with index 3410 (2216 or 428) literally (case insensitive)
 *
 * `mktoErrorDetail` matches the characters `mktoErrorDetail` literally (case insensitive)
 *
 * Non-capturing group `(?:'|"|\\")`
 * * 1st Alternative `'`
 * `'` matches the character `'` with index 3910 (2716 or 478) literally (case insensitive)
 * 2nd Alternative `"`
 * * `"` matches the character `"` with index 3410 (2216 or 428) literally (case insensitive)
 * 3rd Alternative `\\"`
 * * `\\` matches the character `\` with index 9210 (5C16 or 1348) literally (case insensitive)
 * * `"` matches the character `"` with index 3410 (2216 or 428) literally (case insensitive)
 * * `>` matches the character > with index 6210 (3E16 or 768) literally (case insensitive)
 *
 * 2nd Capturing Group `(.*)`
 * * `.` matches any character (except for line terminators)
 * * `*` matches the previous token between zero and unlimited times, as many times as possible, giving back as needed (greedy)
 *
 * Non-capturing group `(?:<\/span>)`
 * `<` matches the character `<` with index 6010 (3C16 or 748) literally (case insensitive)
 * `\/` matches the character `/` with index 4710 (2F16 or 578) literally (case insensitive)
 * `span>` matches the characters `span>` literally (case insensitive)
 */
export const filterMarketoError = /(.*)(?:<span\sclass=(?:'|"|\\")mktoErrorDetail(?:'|"|\\")>)(.*)(?:<\/span>)/gim;

/**
 * regex: `/^https?:\/\/lucid(?:chart|co|edu|scale|spark).directus.app/gmi`
 * `^` asserts position at start of a line
 * `http` matches the characters http literally (case sensitive)
 * `s` matches the character s with index 11510 (7316 or 1638) literally (case sensitive)
 * `:` matches the character : with index 5810 (3A16 or 728) literally (case sensitive)
 * `\/` matches the character / with index 4710 (2F16 or 578) literally (case sensitive)
 * `\/` matches the character / with index 4710 (2F16 or 578) literally (case sensitive)
 * `lucid` matches the characters lucid literally (case sensitive)
 * Non-capturing group `(?:chart|co|edu|scale|spark)`
 * `.` matches any character (except for line terminators)
 * `directus` matches the characters directus literally (case sensitive)
 * `.` matches any character (except for line terminators)
 * `app` matches the characters app literally (case sensitive)
 */
export const isDirectusDomain = /^(https?:\/\/lucid(?:chart|co|edu|spark).directus.app)/im;
